import React, { useState, useCallback } from 'react';
import { Button, message } from 'antd';
import { CodeOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { sendChatMessage } from '../../api/chatApi';
import {
  ChatMessage,
  ChatRequest,
  ChatResponse,
  DebugEntry,
  PageContext,
} from '../../types/chat.types';
import MessageList from './MessageList';
import ChatInput from './ChatInput';
import DebugPanel from './DebugPanel';
import './Chat.css';

/**
 * Props del ChatContainer (v2.0).
 * pageContext permite pre-cargar datos CRM según el contexto de la página.
 */
interface ChatContainerProps {
  /** Contexto de página para pre-cargar datos CRM (v2.0) */
  pageContext?: PageContext;
}

const ChatContainer: React.FC<ChatContainerProps> = ({ pageContext }) => {
  const { userInfo } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [turn, setTurn] = useState(0);
  const [responseId, setResponseId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [debugEntries, setDebugEntries] = useState<DebugEntry[]>([]);
  const [isDebugVisible, setIsDebugVisible] = useState(false);

  const userName = userInfo?.name || 'Usuario';

  const addDebugEntry = useCallback((type: DebugEntry['type'], data: unknown) => {
    setDebugEntries((prev) => [
      ...prev,
      {
        timestamp: new Date(),
        type,
        data,
      },
    ]);
  }, []);

  const handleSendMessage = useCallback(
    async (text: string) => {
      // Añadir mensaje del usuario
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        text,
        sender: 'user',
        senderName: userName,
        timestamp: new Date(),
        type: 'MESSAGE',
      };
      setMessages((prev) => [...prev, userMessage]);

      // Preparar request con previousResponseId para mantener contexto
      // v2.0: Incluye pageContext para pre-carga de datos CRM
      const request: ChatRequest = {
        message: { text },
        session: sessionId
          ? { sessionId, turn: turn + 1, previousResponseId: responseId ?? undefined }
          : undefined,
        context: pageContext ? { page: pageContext } : undefined,
      };

      // Log request en debug panel
      addDebugEntry('request', request);

      setIsLoading(true);

      try {
        const response: ChatResponse = await sendChatMessage(request);

        // Log response en debug panel
        addDebugEntry('response', response);

        // Actualizar sesión y responseId
        setSessionId(response.session.sessionId);
        setTurn(response.session.turn);
        setResponseId(response.session.responseId ?? null);

        // Añadir respuesta del agente
        const agentMessage: ChatMessage = {
          id: `agent-${Date.now()}`,
          text: response.reply.text,
          sender: 'agent',
          senderName: 'Agente Kardio',
          timestamp: new Date(),
          type: response.reply.type as ChatMessage['type'],
        };
        setMessages((prev) => [...prev, agentMessage]);
      } catch (error: unknown) {
        // Log error en debug panel
        addDebugEntry('error', error);

        // Mostrar mensaje de error
        const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
        message.error(`Error al enviar mensaje: ${errorMessage}`);

        // Añadir mensaje de error del sistema
        const errorChatMessage: ChatMessage = {
          id: `error-${Date.now()}`,
          text: 'Lo siento, ha ocurrido un error al procesar tu mensaje. Por favor, inténtalo de nuevo.',
          sender: 'agent',
          senderName: 'Agente Kardio',
          timestamp: new Date(),
          type: 'MESSAGE',
        };
        setMessages((prev) => [...prev, errorChatMessage]);
      } finally {
        setIsLoading(false);
      }
    },
    [userName, sessionId, turn, responseId, pageContext, addDebugEntry]
  );

  const toggleDebugPanel = () => {
    setIsDebugVisible((prev) => !prev);
  };

  return (
    <div className="chat-container">
      <div className="chat-main">
        <div className="chat-header">
          <Button
            type="text"
            icon={<CodeOutlined />}
            onClick={toggleDebugPanel}
            className="debug-toggle-button"
            title={isDebugVisible ? 'Ocultar panel debug' : 'Mostrar panel debug'}
          />
        </div>
        <MessageList messages={messages} isLoading={isLoading} />
        <ChatInput onSend={handleSendMessage} disabled={isLoading} />
      </div>
      {isDebugVisible && (
        <div className="chat-debug">
          <DebugPanel entries={debugEntries} />
        </div>
      )}
    </div>
  );
};

export default ChatContainer;
